import React from 'react';
import PropTypes from 'prop-types';

// Components
import Box from 'relements/build/esm/ui/box';
import Icon from 'relements/build/esm/ui/icon';
import Loader from 'relements/build/esm/ui/loader';
import Tooltip from 'relements/build/esm/overlays/tooltip';
import ContextMenu from 'relements/build/esm/overlays/contextMenu';

// Icons
import KeyboardIcon from 'relements/build/esm/ui/icons/KeyboardIcon';

// Actions
import {
  saveNodePositions,
  updateCurrentStepPosition,
  createNewStep,
  updateStepSelectedForABTesting,
  updateABTestingRhsState,
  updateABTestingDrawerFlag,
  showConfirmationDialog,
  setABTestModifiedFlag,
  setRHSActive,
  updateGlobalConstantsDrawerFlag,
} from 'actions/BotActions';

import { updateGraph } from 'actions/ApiAction';
import { navToSubStory } from 'actions/SubstoryActions';

import {
  sendGoogleAnalyticsEvents,
  createAlert,
  isNewPipeline,
  CASName,
} from '@src/actions/_utils';

// Constants
import {
  STORAGE_KEYS,
  GRAPH_NODE_TYPE,
  AlertTypes,
  GRAPH_NODE_DISPLAY_TEXT,
  CONTEXT_CLEAR_NODE,
  END_OF_QUERY,
} from 'constants/MogamboConstants';
import {
  GRAPH_SHOW_INACTIVE_NODES_TOOLTIP,
  GRAPH_HIDE_INACTIVE_NODES_TOOLTIP,
} from 'constants/Tooltips';
import { GOOGLE_ANALYTICS, MIX_PANEL, ENVIRONMENT } from '@src/const';
import GraphStore from '@src/stores/GraphStore';
import BotStore from '@src/store';
import CommonStore from '@src/stores/CommonStore';

// Api
import * as Api from '@src/api';

// Utils
import {
  storeUserPreference,
  getUserPreference,
} from '@src/utils/UserPrefsUtils';
import { createNewConnection } from '@src/containers/Connection/store/actions';
import * as d3 from 'd3';
import { RHS_STATES } from '@src/components/ABTestRHS/const';

import {
  getDropPosition,
  snapToGrid,
  graphEventStorage,
  roundedRectangle,
  getWarningInfo,
} from './utils';

import { fetchGraph } from './maps';

import GraphStepsSelection from './GraphStepsSelection';

import GraphButton from '../GraphButton';
import NodeWarning from '../NodeWarning';
import KeyboardShortcuts from '../KeyboardShortcuts';

// SEE https://github.com/susielu/d3-legend/issues/62
// Fixes d3Element.transition is not a function
import 'd3-transition';

import {
  state,
  CONSTANTS,
  VISIBLE_NODE_WIDTH,
  VISIBLE_NODE_HEIGHT,
  VISIBLE_NODE_X,
  VISIBLE_NODE_Y,
  NODE_TITLE_Y,
  NODE_CIRCLE_INDICATOR_Y,
  NODE_CIRCLE_INDICATOR_RIGHT_X,
  SVG,
} from './constants';

import RecenterIcon from './recenter_icon.svg';
import RecenterWWIcon from './recenter.svg';
import HideIcon from './hide.svg';
import ShowIcon from './show.svg';

class GraphView extends React.Component {
  constructor(props) {
    super(props);
    this.grid = null;
    this.idMap = null;
    this.posMap = [];
    this.assignzoom = null;

    this.tooltipDiv = null;
    this.contextMenuTrigger = null;
    this.connectFrom = '';
    this.contextConnectionData = { from: '', to: '' };
    this.warningIconText = '';
    this.contextConnectionTriggered = false;

    this.GraphStepsSelection = null;

    this.state = {
      inactiveNodesVisible: getUserPreference(
        STORAGE_KEYS.INACTIVE_NODES_VISIBLE,
        false
      ),
      contextMenuActive: false,
      contextLoaderActive: false,
      warningIconActive: false,
      isKeyboardShortcutsActive: false,
    };

    this.setNodePosition = this.setNodePosition.bind(this);
  }

  setNodePosition(nodeId, posX, posY, prevPosX, prevPosY) {
    if (this.posMap[prevPosX]) {
      this.posMap[prevPosX][prevPosY] = null;
    }

    if (!this.posMap[posX]) {
      this.posMap[posX] = [];
    }

    this.posMap[posX][posY] = nodeId;
  }

  isPositionOccupiedByNode(posX, posY, nodeId) {
    return (
      this.isPositionOccupied(posX, posY) ||
      this.isPositionOccupied(posX - CONSTANTS.grid_size * 1, posY, nodeId) ||
      this.isPositionOccupied(posX - CONSTANTS.grid_size * 2, posY, nodeId) ||
      this.isPositionOccupied(posX + CONSTANTS.grid_size * 1, posY, nodeId) ||
      this.isPositionOccupied(posX + CONSTANTS.grid_size * 2, posY, nodeId)
    );
  }

  getNextEmptyPosition(x, y) {
    let snappedPos = snapToGrid(x, y);
    while (this.isPositionOccupiedByNode(snappedPos.x, snappedPos.y)) {
      snappedPos = snapToGrid(snappedPos.x, snappedPos.y + CONSTANTS.grid_size);
    }

    return snappedPos;
  }

  isPositionOccupied(posX, posY, nodeId) {
    if (this.posMap[posX]) {
      return this.posMap[posX][posY]
        ? this.posMap[posX][posY] !== nodeId
        : false;
    }

    return false;
  }

  getConnectionPath(source, target) {
    let points;
    if (source.x === target.x && source.y === target.y) {
      const deltaX = VISIBLE_NODE_WIDTH / 2 + 30;
      const deltaY = VISIBLE_NODE_HEIGHT / 2 + 30;
      points = [
        [source.x, source.y],
        [source.x + deltaX, source.y],
        [target.x + deltaX, target.y + deltaY],
        [target.x, target.y + deltaY],
        [target.x, target.y],
      ];
      return d3.line().curve(d3.curveCardinal.tension(0.5))(points);
    }
    const delta = (target.x - source.x) * 0.4;
    points = [
      [source.x, source.y],
      [source.x + delta, source.y],
      [target.x - delta, target.y],
      [target.x, target.y],
    ];
    return d3.line().curve(d3.curveBasis)(points);
  }

  showTooltip(tooltipType, data, posX, posY) {
    let tooltipContent = '';

    switch (tooltipType) {
      case 'node_tooltip':
        tooltipContent = `${data.title}<br/>`;
        break;

      case 'connection_tooltip':
        tooltipContent = `From: <b>${data.source.title}</b><br />To: <b>${data.target.title}</b><br/>`;
        break;

      case 'api_tooltip':
        tooltipContent = `${data.apiFunction}<br/>`;
        break;

      default:
        break;
    }

    this.tooltipDiv
      .html(tooltipContent)
      .style('left', `${posX}px`)
      .style('top', `${posY}px`);

    this.tooltipDiv
      .transition()
      .duration(200)
      .style('opacity', 0.9)
      .style('background', 'rgba(66, 139, 202, 0.2)')
      .style('visibility', 'visible');
  }

  moveTooltip(posX, posY) {
    this.tooltipDiv.style('left', `${posX}px`).style('top', `${posY}px`);
  }

  hideTooltip() {
    this.tooltipDiv
      .transition()
      .duration(500)
      .style('opacity', 0)
      .style('visibility', 'hidden');
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Required for Skill set Level Graphs
    if (this.props.graph.length !== nextProps.graph.length) {
      return true;
    }

    for (const i in this.props.graph) {
      if (this.props.graph[i].nodeList && nextProps.graph[i].nodeList) {
        if (
          this.props.graph[i].nodeList.length !==
          nextProps.graph[i].nodeList.length
        ) {
          return true;
        }

        for (const j in this.props.graph[i].nodeList) {
          if (
            this.props.graph[i].nodeList[j] !== nextProps.graph[i].nodeList[j]
          ) {
            return true;
          }
        }
      }
    }

    if (this.state.inactiveNodesVisible !== nextState.inactiveNodesVisible) {
      return true;
    }

    if (this.props.connection !== nextProps.connection) {
      return true;
    }

    if (this.props.allSteps !== nextProps.allSteps) {
      return true;
    }

    if (
      this.state.isKeyboardShortcutsActive !==
      nextState.isKeyboardShortcutsActive
    ) {
      return true;
    }
    return false;
  }

  createNodes(stepEnter) {
    const mixPanelTracker = BotStore.getMixPanel();
    const _this = this;
    const gu = stepEnter
      .append('g')
      .html((d) => {
        return `
          <defs>
              <linearGradient id="gradient-${d.stepId}" gradientTransform="rotate(90)">
                <stop offset="100%"  stop-color="white" />
              </linearGradient>
              <linearGradient id="gradient-highlighted" gradientTransform="rotate(90)">
                <stop offset="100%" stop-color="white" />
              </linearGradient>
              <linearGradient id="gradient-inactive-${d.stepId}" gradientTransform="rotate(90)">
                <stop offset="100%"  stop-color="white" />
              </linearGradient>
          </defs>
        `;
      })
      .style('--stroke-color', '#DDDDDD')
      .style('--color-black', 'black')
      .style('--primary-color', (d) => d.fillColor)
      .style('--text-color', (d) => (d.active ? '#333' : '#aaa'))
      .style('--type-color', 'black')
      .style('--border-color', (d) =>
        d.active ? d.fillColor : d.inactiveColor
      )
      .style('--indicator-color', (d) => (d.active ? '#333' : '#aaa'))
      .style('--secondary-color', (d) => d.titleColor)
      .attr('class', (d) => `substory-${d.subStoryId}`)
      .classed('steps', true)
      .classed('inactive-steps', (d) => !d.active)
      .classed('inactive', (d) => !d.active)
      .classed('hidden', (d) => !d.active && !this.state.inactiveNodesVisible)
      .classed('substory-inactive', (d) => !d.subStoryActive)
      .classed('is-highlighted', (d) => d.highlighted)
      .attr('id', (d) => `node-${d.stepId}`)
      .attr('transform', (d) => `translate(${d.x},${d.y})`)
      .on('mouseup', (d) => {
        if (d3.event.defaultPrevented) {
          return;
        }

        if (
          state.dragLink.active &&
          state.dragLink.startNode.stepId !== d.stepId &&
          !state.dragLink.startNode.stepId.startsWith('new') &&
          !d.stepId.startsWith('new')
        ) {
          createNewConnection(
            GRAPH_NODE_TYPE[state.dragLink.startNode.type],
            state.dragLink.startNode.stepId,
            GRAPH_NODE_TYPE[d.type],
            d.stepId,
            this.props.currentLanguage.languageSource,
            this.props.currentLanguage.languageScript,
            this.props?.meta?.domain?.title || this.props.domain.title
          )
            .then(() => {
              this.props.onConnectionClick(state.dragLink.startNode, d);
              this.createConnection(state.dragLink.startNode.stepId, d.stepId);
            })
            .catch((error) => {
              createAlert(error, null, AlertTypes.NEGATIVE);
            });

          state.dragLink.active = false;
        }
        d3.event.stopPropagation();
      })
      .on('mousedown', (d) => {
        if (d3.event.defaultPrevented) {
          return;
        }

        if (d3.event.shiftKey) {
          state.dragLink.active = true;
          state.dragLink.startNode = d;
          _this.GraphStepsSelection?.resetSelectedSteps();
        }
      })
      .on('mouseover', (d) => {
        const selectedSteps = this.GraphStepsSelection?.getSelectedSteps();

        (d.sourceList || []).forEach((stepId) => {
          if (!selectedSteps?.[stepId]) {
            // this is because we want to get color of source node
            // so we filter from allNodes and get the color
            const sourceStep = this.props.allSteps.filter(
              (step) => step.id === stepId
            );
            d3.select(`#gradient-${stepId} > stop:first-child`).attr(
              'stop-color',
              sourceStep.length === 1 ? sourceStep[0]?.color?.primary : '#fff'
            );
            d3.select(`#gradient-inactive-${stepId} > stop:first-child`).attr(
              'stop-color',
              sourceStep.length === 1
                ? sourceStep[0]?.color?.transparent
                : '#fff'
            );
            d3.select(`#node-${stepId}`)
              .style('--text-color', '#FFFFFF')
              .style('--type-color', 'white')
              .style('--primary-color', (d) => {
                if (d.type !== GRAPH_NODE_DISPLAY_TEXT.AISS)
                  return `var(--color-black)`;
              })
              .style('--secondary-color', '#FFFFFF');
            d3.select(`#link-${stepId}-${d.stepId}`).classed(
              'selected-link',
              true
            );
            d3.select(`#arrow-${stepId}-${d.stepId}`).classed(
              'selected-arrow',
              true
            );
          }
        });

        (d.destinationList || []).forEach((stepId) => {
          if (!selectedSteps?.[stepId]) {
            const destStep = this.props.allSteps.filter(
              (step) => step.id === stepId
            );
            d3.select(`#gradient-${stepId} > stop:first-child`).attr(
              'stop-color',
              destStep.length === 1 ? destStep[0]?.color?.primary : '#fff'
            );
            d3.select(`#gradient-inactive-${stepId} > stop:first-child`).attr(
              'stop-color',
              destStep.length === 1 ? destStep[0]?.color?.transparent : '#fff'
            );
            d3.select(`#node-${stepId}`)
              .style('--text-color', '#FFFFFF')
              .style('--type-color', 'white')
              .style('--primary-color', `var(--color-black)`)
              .style('--secondary-color', '#FFFFFF');
            d3.select(`#link-${d.stepId}-${stepId}`).classed(
              'selected-link',
              true
            );
            d3.select(`#arrow-${d.stepId}-${stepId}`).classed(
              'selected-arrow',
              true
            );
          }
        });

        d3.select(`#gradient-${d.stepId} > stop:first-child`).attr(
          'stop-color',
          selectedSteps?.[d.stepId]
            ? CONSTANTS.node_selected_fill_color
            : d.fillColor
        );
        d3.select(`#gradient-inactive-${d.stepId} > stop:first-child`).attr(
          'stop-color',
          selectedSteps?.[d.stepId]
            ? CONSTANTS.node_selected_fill_color
            : d.inactiveColor
        );
        d3.select(`#node-${d.stepId}`)
          .style('--text-color', selectedSteps?.[d.stepId] ? '#333' : '#FFFFFF')
          .style('--type-color', 'white')
          .style('--stroke-color', 'none')
          .style('--primary-color', '#000000')
          .style('--secondary-color', '#000');

        d3.selectAll(`.affordance-${d.stepId}`).style('visibility', 'visible');

        if (
          d.type !== GRAPH_NODE_DISPLAY_TEXT.AISS &&
          !_this.nodeCreationActive &&
          !d.endNode &&
          !Array.from(document.querySelectorAll('g')).find((el) =>
            el.id.includes('node-new')
          )
        ) {
          d3.selectAll(`.context-${d.stepId}`).attr(
            'class',
            `context-${d.stepId} context contextActive`
          );
        }
      })
      .on('mouseout', (d) => {
        if (!_this.state.contextMenuActive) {
          (d.sourceList || []).forEach((stepId) => {
            d3.select(`#gradient-${stepId} > stop:first-child`).attr(
              'stop-color',
              this.GraphStepsSelection?.getSelectedSteps()?.[stepId]
                ? CONSTANTS.node_selected_fill_color
                : 'white'
            );
            d3.select(`#gradient-inactive-${stepId} > stop:first-child`).attr(
              'stop-color',
              this.GraphStepsSelection?.getSelectedSteps()?.[stepId]
                ? CONSTANTS.node_selected_fill_color
                : 'white'
            );
            d3.select(`#node-${stepId}`)
              .style('--text-color', () => {
                // this is because we want to get color of source node
                // so we filter from allNodes and get the color
                const sourceStep = this.props.allSteps.filter(
                  (step) => step.id === stepId
                );

                if (sourceStep.length === 1 && !sourceStep[0].active) {
                  return '#aaa';
                }

                return '#333';
              })
              .style('--type-color', 'black')
              .style('--primary-color', d.fillColor)
              .style('--secondary-color', d.titleColor);
            d3.select(`#link-${stepId}-${d.stepId}`).classed(
              'selected-link',
              !!d.highlighted
            );
            d3.select(`#arrow-${stepId}-${d.stepId}`).classed(
              'selected-arrow',
              !!d.highlighted
            );
          });

          (d.destinationList || []).forEach((stepId) => {
            d3.select(`#gradient-${stepId} > stop:first-child`).attr(
              'stop-color',
              this.GraphStepsSelection?.getSelectedSteps()?.[stepId]
                ? CONSTANTS.node_selected_fill_color
                : 'white'
            );
            d3.select(`#gradient-inactive-${stepId} > stop:first-child`).attr(
              'stop-color',
              this.GraphStepsSelection?.getSelectedSteps()?.[stepId]
                ? CONSTANTS.node_selected_fill_color
                : 'white'
            );
            d3.select(`#node-${stepId}`)
              .style('--text-color', () => {
                // this is because we want to get color of destination node
                // so we filter from allNodes and get the color
                const destStep = this.props.allSteps.filter(
                  (step) => step.id === stepId
                );

                if (destStep.length === 1 && !destStep[0].active) {
                  return '#aaa';
                }

                return '#333';
              })
              .style('--type-color', 'black')
              .style('--primary-color', d.fillColor)
              .style('--secondary-color', d.titleColor);
            d3.select(`#link-${d.stepId}-${stepId}`).classed(
              'selected-link',
              !!d.highlighted
            );
            d3.select(`#arrow-${d.stepId}-${stepId}`).classed(
              'selected-arrow',
              !!d.highlighted
            );
          });

          d3.select(`#gradient-${d.stepId} > stop:first-child`).attr(
            'stop-color',
            this.GraphStepsSelection?.getSelectedSteps()?.[d.stepId]
              ? CONSTANTS.node_selected_fill_color
              : 'white'
          );
          d3.select(`#gradient-inactive-${d.stepId} > stop:first-child`).attr(
            'stop-color',
            this.GraphStepsSelection?.getSelectedSteps()?.[d.stepId]
              ? CONSTANTS.node_selected_fill_color
              : 'white'
          );
          d3.select(`#node-${d.stepId}`)
            .style('--text-color', d.active ? '#333' : '#aaa')
            .style('--type-color', 'black')
            .style('--stroke-color', '#DDDDDD')
            .style('--primary-color', d.fillColor)
            .style('--secondary-color', d.titleColor);

          d3.selectAll(`.affordance-${d.stepId}`).style('visibility', 'hidden');
        }

        d3.selectAll(`.context-${d.stepId}`).attr(
          'class',
          `context-${d.stepId} context ${
            _this.state.contextMenuActive ? 'contextActive' : ''
          }`
        );

        _this.hideTooltip();
      });

    const nodeElements = gu
      .append('g')
      .style(
        'transform',
        (d) =>
          d.type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE && 'translate(260px,55px)'
      );

    // outer box - visible area
    nodeElements
      .append('path')
      .attr('class', 'outer-box')
      .attr('fill', (d) =>
        d.active
          ? `url('#gradient-${d.stepId}')`
          : `url('#gradient-inactive-${d.stepId}')`
      )
      .attr('d', (d) => {
        const hexPath = 'M 40 100 L 100 40 H 500 L 560 100 L 500 160 H 100 Z';
        let radius = CONSTANTS.corner_radius;
        let isTopLeftRounded = true;
        let isBottomRightRounded = true;
        // this check is because we want to create different shapes
        // based on different node types

        if (d.type === GRAPH_NODE_DISPLAY_TEXT.AISS) return hexPath;

        if (d.type === GRAPH_NODE_DISPLAY_TEXT.STATIC) {
          isTopLeftRounded = false;
          isBottomRightRounded = false;
        } else if (d.type === GRAPH_NODE_DISPLAY_TEXT.OUTPUT) {
          radius = CONSTANTS.corner_output_radius;
        } else if (d.type === GRAPH_NODE_DISPLAY_TEXT.CODE) {
          radius = CONSTANTS.corner_code_radius;
        }

        return roundedRectangle(
          VISIBLE_NODE_X,
          VISIBLE_NODE_Y,
          VISIBLE_NODE_WIDTH,
          VISIBLE_NODE_HEIGHT,
          radius,
          isTopLeftRounded,
          true,
          true,
          isBottomRightRounded
        );
      })

      .attr('stroke', (d) =>
        this.GraphStepsSelection?.getSelectedSteps()?.[d.stepId]
          ? `${CONSTANTS.node_selected_stroke_color}${!d.active ? 54 : ''}`
          : 'var(--border-color)'
      )
      .attr('stroke-width', (d) =>
        d.type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE ? '0' : '15'
      )
      .on('click', (d) => {
        if (
          CommonStore.getABTestingDrawerFlag() &&
          CommonStore.getABTestingRhsState() === RHS_STATES.TEST_STARTED
        ) {
          if (CommonStore.getABTestModifiedFlag()) {
            showConfirmationDialog({
              okTitle: 'Leave',
              cancelTitle: 'Keep Editing',
              title: 'Are you sure you want to leave?',
              message:
                "It looks like you're still working on this. You may lose the info you've entered so far if you leave now.",
              onOk: () => {
                updateABTestingDrawerFlag(false);
                this.props.onNodeClick(d.stepId, d.title, d.subStoryId, d.type);
                setABTestModifiedFlag(false);
              },
              onCancel: () => {},
              okActionType: 'danger',
            });
          } else updateABTestingDrawerFlag(false);
        }

        if (
          CommonStore.getABTestingDrawerFlag() &&
          CommonStore.getABTestingRhsState() !== RHS_STATES.TEST_STARTED
        ) {
          if (!d.active) {
            createAlert(
              'You cannot split a deactivated node',
              '',
              AlertTypes.NEGATIVE
            );
            d3.event.stopPropagation();
            return;
          }

          if (
            d.type === GRAPH_NODE_DISPLAY_TEXT.OUTPUT ||
            d.type === GRAPH_NODE_DISPLAY_TEXT.CODE
          ) {
            createAlert(
              'You can only split a static node',
              '',
              AlertTypes.NEGATIVE
            );
            d3.event.stopPropagation();
            return;
          }
          if (CommonStore.getABTestingRhsState() === RHS_STATES.INITIAL) {
            updateABTestingRhsState(RHS_STATES.STEP_SELECTED);
          }
          this.GraphStepsSelection.onStepClick(
            d3.event.metaKey || d3.event.ctrlKey,
            d,
            () => {
              this.props.onNodeClick(d.stepId, d.title, d.subStoryId, d.type);
            }
          );
          updateStepSelectedForABTesting(d);
          return;
        }

        if (_this.props.nodeContextMenuEnabled && _this.connectFrom) {
          _this.refresh();
          _this.connectFrom = '';
          _this.nodeCreationActive = false;
        }

        if (d3.event.defaultPrevented) {
          return;
        }

        if (this.GraphStepsSelection) {
          this.GraphStepsSelection.onStepClick(
            d3.event.metaKey || d3.event.ctrlKey,
            d,
            () => {
              if (d3.event.metaKey || d3.event.ctrlKey) {
                if (BotStore.getRHSActive()) {
                  navToSubStory(this.props, d.subStoryId);
                  BotStore.setRHSActive(false);
                }
                return;
              }

              if (d3.event.shiftKey || d3.event.altKey) {
                return;
              }
              if (!CommonStore.getABTestModifiedFlag()) {
                this.props.onNodeClick(d.stepId, d.title, d.subStoryId, d.type);
              }
              d3.event.stopPropagation();
            }
          );
          return;
        }

        if (d3.event.ctrlKey || d3.event.shiftKey || d3.event.altKey) {
          return;
        }
        if (!CommonStore.getABTestModifiedFlag()) {
          this.props.onNodeClick(d.stepId, d.title, d.subStoryId, d.type);
        }
        d3.event.stopPropagation();
      });

    const abNodes = nodeElements.filter(
      (d) => d.type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE
    );

    abNodes
      .html(() => {
        return `${SVG.AB}`;
      })
      .on('click', () => {
        if (!CommonStore.getABTestingDrawerFlag()) {
          updateABTestingRhsState(RHS_STATES.TEST_STARTED);
          updateGlobalConstantsDrawerFlag(false);
          setRHSActive(true);
          updateABTestingDrawerFlag(true);
          mixPanelTracker.track({
            event: MIX_PANEL.AB_TEST.RHS_OPENED,
            payload: {
              is_AB_Test_already_setup: true,
            },
          });
        }
      });

    const nonABNodes = nodeElements.filter(
      (d) => d.type !== GRAPH_NODE_DISPLAY_TEXT.AB_NODE
    );

    // Step title
    const textSpan = nonABNodes
      .append('text')
      .attr('fill', 'var(--text-color)')
      .attr('class', 'label')
      .attr('dominant-baseline', 'baseline')
      .attr('font-size', `${CONSTANTS.title_font_size}px`);

    textSpan
      .append('tspan')
      .attr('x', CONSTANTS.node_width / 2 - 7)
      .attr('y', (d) => {
        // we adjust the y coordinate depending on whether text is in capitals or not
        let titleLengthGreaterDiff = -20;
        let titleLengthLesserDiff = 0;
        if (d.type === GRAPH_NODE_DISPLAY_TEXT.AISS) {
          titleLengthGreaterDiff = -12;
          titleLengthLesserDiff = 10;
        }
        if (d.title.toUpperCase() === d.title) {
          return d.title.length > CONSTANTS.capital_string_max_length
            ? NODE_TITLE_Y + titleLengthGreaterDiff
            : NODE_TITLE_Y + titleLengthLesserDiff;
        }

        return d.title.length > CONSTANTS.string_max_length
          ? NODE_TITLE_Y + titleLengthGreaterDiff
          : NODE_TITLE_Y + titleLengthLesserDiff;
      })
      .text((d) => {
        let text;

        // we max length of the string depending on whether text is in capitals or not
        if (d.title.toUpperCase() === d.title) {
          if (d.title.length > CONSTANTS.capital_string_max_length) {
            text = `${d.title.substring(
              0,
              CONSTANTS.capital_string_max_length
            )}`;
          } else {
            text = d.title;
          }
        } else if (d.title.length > CONSTANTS.string_max_length) {
          text = `${d.title.substring(0, CONSTANTS.string_max_length)}`;
        } else {
          text = d.title;
        }

        return text;
      });

    textSpan
      .append('tspan')
      .attr('x', CONSTANTS.node_width / 2 - 9)
      .attr('y', (d) => {
        // we adjust the y coordinate depending on whether text is in capitals or not
        if (d.title.toUpperCase() === d.title) {
          return d.title.length > CONSTANTS.capital_string_max_length
            ? NODE_TITLE_Y - 20
            : NODE_TITLE_Y;
        }

        return d.title.length > CONSTANTS.string_max_length
          ? NODE_TITLE_Y - 20
          : NODE_TITLE_Y;
      })
      .attr('dy', '1.2em')
      .text((d) => {
        let text = '';

        // we max length of the string depending on whether text is in capitals or not
        if (d.title.toUpperCase() === d.title) {
          if (
            d.title.length >
            CONSTANTS.capital_string_max_length +
              CONSTANTS.capital_string_max_length
          ) {
            text = `${d.title.substring(
              CONSTANTS.capital_string_max_length,
              CONSTANTS.capital_string_max_length +
                CONSTANTS.capital_string_max_length
            )}...`;
          } else if (d.title.length > CONSTANTS.capital_string_max_length) {
            text = `${d.title.substring(CONSTANTS.capital_string_max_length)}`;
          }
        } else if (
          d.title.length >
          CONSTANTS.string_max_length + CONSTANTS.string_max_length
        ) {
          text = `${d.title.substring(
            CONSTANTS.string_max_length,
            CONSTANTS.string_max_length + CONSTANTS.string_max_length
          )}...`;
        } else if (d.title.length > CONSTANTS.string_max_length) {
          text = `${d.title.substring(CONSTANTS.string_max_length)}`;
        }

        return text;
      });

    // Start Node Indicator
    nonABNodes
      .append('path')
      .attr('fill', 'var(--indicator-color)')
      .attr('visibility', (d) => {
        return d.startNode ? 'visible' : 'hidden';
      })
      .attr('d', (d) => {
        const showStartPath =
          d.type === GRAPH_NODE_DISPLAY_TEXT.AISS
            ? `M93.342,39.279l-56,56c-2.3,2.3-2.3,6.1,0,8.401l56,56c3.801,3.8,10.2,1.1,10.2-4.2v-112
      C105.542,38.179,99.143,36.479,93.342,39.279z`
            : roundedRectangle(
                VISIBLE_NODE_X - 8,
                NODE_CIRCLE_INDICATOR_Y,
                CONSTANTS.indicator_width,
                VISIBLE_NODE_HEIGHT + 14,
                45,
                false,
                false,
                true,
                false
              );
        return showStartPath;
      })
      .attr('stroke', (d) => d.type === GRAPH_NODE_DISPLAY_TEXT.AISS && '#333')
      .attr(
        'stroke-width',
        (d) => d.type === GRAPH_NODE_DISPLAY_TEXT.AISS && '10'
      )
      .classed('start-circle', true);

    nonABNodes
      .append('rect')
      .attr('id', (d) => `outer-border-box-${d.stepId}`)
      .attr('fill', 'none')
      .attr('stroke', '#0056B8')
      .attr('stroke-width', '5')
      .attr('visibility', (d) =>
        this.GraphStepsSelection?.getSelectedSteps()?.[d.stepId]
          ? 'visible'
          : 'hidden'
      )
      .attr('width', VISIBLE_NODE_WIDTH + 20)
      .attr('height', VISIBLE_NODE_HEIGHT + 18)
      .attr('x', 50)
      .attr('y', 12);

    nonABNodes
      .append('text')
      .text('S')
      .attr('dominant-baseline', 'central')
      .attr('text-anchor', 'middle')
      .style('font-size', `${CONSTANTS.indicator_font_size}px`)
      .attr('visibility', (d) => {
        return d.startNode ? 'visible' : 'hidden';
      })
      .attr('x', (d) => {
        return (
          VISIBLE_NODE_X + (d.type === GRAPH_NODE_DISPLAY_TEXT.AISS ? 12 : 20)
        );
      })
      .attr('y', (d) => {
        return (
          NODE_CIRCLE_INDICATOR_Y -
          1 +
          (VISIBLE_NODE_HEIGHT + 5) /
            (d.type === GRAPH_NODE_DISPLAY_TEXT.AISS ? 1.7 : 2)
        );
      })
      .attr('fill', '#fff')
      .classed('start-text', true);

    // End Node Indicator
    nonABNodes
      .append('path')
      .attr('fill', 'var(--indicator-color)')
      .attr('visibility', (d) => {
        return d.endNode ? 'visible' : 'hidden';
      })
      .attr('d', (d) => {
        // these MAGIC NUMBERS below are all calculated by hit and trials
        // hence they are not converted to constants, since they're not used in multiple places
        // and there's no logic behind them
        // TODO: figure out the MAGIC NUMBERS mess (most likely will be a graph rewrite)
        let radius = 15;
        let height = VISIBLE_NODE_HEIGHT + 15;
        let isBottomRightRounded = true;

        if (d.type === GRAPH_NODE_DISPLAY_TEXT.STATIC) {
          isBottomRightRounded = false;
          radius = 42;
          height = VISIBLE_NODE_HEIGHT + 15;
        }

        return roundedRectangle(
          NODE_CIRCLE_INDICATOR_RIGHT_X - 12,
          NODE_CIRCLE_INDICATOR_Y,
          CONSTANTS.indicator_width,
          height,
          radius,
          false,
          true,
          false,
          isBottomRightRounded
        );
      })
      .classed('end-circle', true);

    const nodeContextEnabled = BotStore.getEnableNodeContextMenuFlag();
    nonABNodes
      .append('text')
      .text((d) => {
        return nodeContextEnabled && d[END_OF_QUERY.KEY]
          ? END_OF_QUERY.NODE_GRAPH_DENOTION
          : CONTEXT_CLEAR_NODE.NODE_GRAPH_DENOTION;
      })
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .style('font-size', `${CONSTANTS.indicator_font_size}px`)
      .attr('visibility', (d) => {
        return d.endNode ? 'visible' : 'hidden';
      })
      .attr('x', NODE_CIRCLE_INDICATOR_RIGHT_X + 12)
      .attr('y', NODE_CIRCLE_INDICATOR_Y + (VISIBLE_NODE_HEIGHT + 5) / 2)
      .attr('fill', '#fff')
      .classed('start-end-text', true)
      .classed('end-text', true);

    // #region Context Menu trigger
    if (_this.props.nodeContextMenuEnabled || nodeContextEnabled) {
      nonABNodes
        .append('path')
        .attr(
          'class',
          (d) =>
            `context-${d.stepId} context ${
              _this.state.contextMenuActive &&
              d.type === GRAPH_NODE_DISPLAY_TEXT.STATIC
                ? 'contextActive'
                : ''
            }`
        )
        .attr('fill', 'var(--indicator-color)')
        .attr('d', (d) => {
          let radius = CONSTANTS.corner_radius;
          let isTopLeftRounded = true;
          let isBottomRightRounded = true;
          let height = VISIBLE_NODE_HEIGHT + 15;
          let y = NODE_CIRCLE_INDICATOR_Y;

          // this check is because we want to create different shapes
          // based on different node types
          if (d.type === GRAPH_NODE_DISPLAY_TEXT.STATIC) {
            isTopLeftRounded = false;
            isBottomRightRounded = false;
          } else if (d.type === GRAPH_NODE_DISPLAY_TEXT.OUTPUT) {
            radius = CONSTANTS.corner_output_radius;
            isTopLeftRounded = false;
          } else if (d.type === GRAPH_NODE_DISPLAY_TEXT.CODE) {
            radius = CONSTANTS.corner_code_radius;
            isTopLeftRounded = false;
            height = VISIBLE_NODE_HEIGHT + 10;
            y = NODE_CIRCLE_INDICATOR_Y + 2;
          }

          return roundedRectangle(
            NODE_CIRCLE_INDICATOR_RIGHT_X - 12,
            y,
            CONSTANTS.indicator_width,
            height,
            radius,
            isTopLeftRounded,
            true,
            false,
            isBottomRightRounded
          );
        })
        .on('click', (d) => {
          _this.toggleContextMenu(
            document.getElementsByClassName(`context-${d.stepId}`)[0],
            true,
            d.stepId,
            d.type
          );
        });

      nonABNodes
        .append('text')
        .attr('class', (d) => `context-${d.stepId} context`)
        .text('+')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .style('font-size', `45px`)
        .attr('x', NODE_CIRCLE_INDICATOR_RIGHT_X + 12)
        .attr('y', NODE_CIRCLE_INDICATOR_Y + (VISIBLE_NODE_HEIGHT + 5) / 2)
        .attr('fill', '#fff')
        .on('click', (d) => {
          this.toggleContextMenu(
            document.getElementsByClassName(`context-${d.stepId}`)[0],
            true,
            d.stepId,
            d.type
          );
        });
    }
    // #endregion

    const apiIndicatorGroup = nonABNodes
      .append('g')
      .attr('transform', `scale(1.5)`)
      .attr('transform', `translate(1.5)`)
      .attr('class', 'api-integration-indicator')
      .style('transform', (d) => {
        const warningInfo = getWarningInfo(d);
        const warningVisible =
          warningInfo.displayWarningIcon && !d.stepId.includes('new');
        if (warningVisible) return 'translate(-20px, 92px) scale(1.5)';
        return 'translate(-20px) scale(1.5)';
      })
      .style('visibility', (d) => (d.apiFunction ? 'visible' : 'hidden'));

    apiIndicatorGroup
      .append('path')
      .style('fill', (d) =>
        d.active ? '#FF5D41' : CONSTANTS.node_inactive_color
      )
      .attr(
        'd',
        'M9.88279169,10.2344166 L21.8115605,10.2344166 C27.2696756,10.2344166 31.6943522,14.6590932 31.6943522,20.1172083 C31.6943522,25.5753234 27.2696756,30 21.8115605,30 L9.88279169,30 C4.42467655,30 6.68426323e-16,25.5753234 0,20.1172083 C-6.68426323e-16,14.6590932 4.42467655,10.2344166 9.88279169,10.2344166 Z M25.1162791,5.41822056 C24.7860091,5.41822056 24.5182724,5.14868501 24.5182724,4.81619606 C24.5182724,4.4837071 24.7860091,4.21417155 25.1162791,4.21417155 C32.051948,4.21417155 37.6744186,9.87441816 37.6744186,16.8566862 C37.6744186,17.1891752 37.4066819,17.4587107 37.076412,17.4587107 C36.746142,17.4587107 36.4784053,17.1891752 36.4784053,16.8566862 C36.4784053,10.5393961 31.3914081,5.41822056 25.1162791,5.41822056 Z M25.1162791,1.20404901 C24.7860091,1.20404901 24.5182724,0.934513462 24.5182724,0.602024507 C24.5182724,0.269535553 24.7860091,0 25.1162791,0 L26.910299,0 C35.1670478,-1.06941035e-15 41.8604651,6.73838882 41.8604651,15.0506127 L41.8604651,16.8566862 C41.8604651,17.1891752 41.5927284,17.4587107 41.2624585,17.4587107 C40.9321885,17.4587107 40.6644518,17.1891752 40.6644518,16.8566862 L40.6644518,15.0506127 C40.6644518,7.40336673 34.5065079,1.20404901 26.910299,1.20404901 L25.1162791,1.20404901 Z'
      );

    apiIndicatorGroup
      .append('path')
      .style('fill', '#FFFFFF')
      .attr(
        'd',
        'M13.6485529,23.2232286 L13.0700608,21.5334477 L10.6596773,21.5334477 L10.0768027,23.2232286 L9.06882418,23.2232286 L11.3521148,16.8567694 L12.408301,16.8567694 L14.6915916,23.2232286 L13.6485529,23.2232286 Z M11.8298089,18.0127031 L10.9094807,20.7039991 L12.8202575,20.7039991 L11.9043117,18.0127031 L11.8298089,18.0127031 Z'
      );

    apiIndicatorGroup
      .append('path')
      .style('fill', '#FFFFFF')
      .attr(
        'd',
        'M15.7336972,16.8567694 L18.2098185,16.8567694 C19.4456878,16.8567694 20.2958959,17.7171018 20.2958959,18.9789225 C20.2958959,20.2230954 19.4193927,21.0790157 18.1791409,21.0790157 L16.7153807,21.0790157 L16.7153807,23.2232286 L15.7336972,23.2232286 L15.7336972,16.8567694 Z M16.7153807,17.7303376 L16.7153807,20.2098595 L17.9512501,20.2098595 C18.7970755,20.2098595 19.2922998,19.7686634 19.2922998,18.9789225 C19.2922998,18.1715337 18.8146056,17.7303376 17.9512501,17.7303376 L16.7153807,17.7303376 Z'
      );

    apiIndicatorGroup
      .append('polygon')
      .style('fill', '#FFFFFF')
      .attr(
        'points',
        '22.481838 23.2232286 22.481838 16.8567694 21.5001546 16.8567694 21.5001546 23.2232286'
      );

    // #region Warning Circle
    if (_this.props.nodeContextMenuEnabled || nodeContextEnabled) {
      const warningCircle = nonABNodes
        .append('g')
        .attr('class', (d) => `warning-tooltip-${d.stepId}`)
        .attr('transform', `scale(2.5)`)
        .attr('transform', `translate(4,4)`)
        .attr('stroke-linejoin', 'round')
        .attr('stroke', '#C0771A')
        .attr('stroke-width', '2')
        .style('transform', 'scale(2.2)')
        .attr('visibility', (d) => {
          const warningInfo = getWarningInfo(d);
          return warningInfo.displayWarningIcon && !d.stepId.includes('new')
            ? 'visible'
            : 'hidden';
        })
        .on('mouseover', (d) => {
          if (!_this.state.warningIconActive) {
            _this.toggleWarningTooltip(
              document.getElementsByClassName(`warning-tooltip-${d.stepId}`)[0],
              d
            );
          }
        })
        .on('mouseout', () => {
          if (_this.state.warningIconActive) {
            _this.toggleWarningTooltip(null);
          }
        });

      warningCircle
        .append('path')
        .attr(
          'd',
          'M32 16h0a16 16 0 01-16 16h0A16 16 0 010 16h0A16 16 0 0116 0h0a16 16 0 0116 16z'
        )
        .attr('fill', '#FFBC67');

      warningCircle.append('path').attr('d', 'M16 7v12');

      warningCircle
        .append('circle')
        .attr('cx', '16')
        .attr('cy', '24')
        .attr('r', '1')
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('fill', '#C0771A');

      // warningCircle
      //   .append('circle')
      //   .attr('cx', '16')
      //   .attr('cy', '16')
      //   .attr('r', '15.5')
      //   .attr('fill', '#FEFEFE')
      //   .attr('stroke', '#F6A400');

      // warningCircle
      //   .append('path')
      //   .attr(
      //     'd',
      //     'M14.5075 22.7575C14.5075 23.5825 15.175 24.25 16 24.25C16.825 24.25 17.4925 23.5825 17.4925 22.7575H14.5075ZM21.16 19.615V15.25C21.16 12.8125 19.4725 10.7725 17.1925 10.2325V9.6925C17.1925 9.0325 16.66 8.5 16 8.5C15.34 8.5 14.8075 9.0325 14.8075 9.6925V10.2325C12.5275 10.7725 10.84 12.8125 10.84 15.25V19.615L9.25 21.205V22H22.75V21.205L21.16 19.615ZM19 16.7575H16.75V19.0075H15.25V16.7575H13V15.25H15.25V13H16.75V15.25H19V16.7575Z'
      //   )
      //   .attr('fill', '#F6A400');
    }
    // #endregion

    return gu;
  }

  createLinks(
    normalLinksData,
    normalLinksGroup,
    _this,
    dontClear,
    createdViaContext = false
  ) {
    if (!dontClear) {
      // Clear all existing links in Link Group
      normalLinksGroup.selectAll('g').remove();
    }

    // Add new link groups where each group contains 3 elements
    // 1. An invisible link with thick stroke-width
    // 2. A visible link
    // 3. An arrow in the middle of the link indicating the connection direction
    const linksList = [];
    normalLinksData.forEach((linkData) => {
      // Group for a link
      const linkGroup = normalLinksGroup
        .append('g')
        .datum(linkData)
        .attr(
          'class',
          (d) =>
            `link-wrapper substory-${d.subStoryId} substory-${d.destinationSubStoryId}`
        )
        .classed('inactive', (d) => !d.active)
        .classed('hidden', (d) => !d.active && !this.state.inactiveNodesVisible)
        .classed('substory-inactive', (d) => !d.subStoryActive);
      linksList.push(linkGroup);

      // Adding the visible link to the group
      linkGroup
        .append('path')
        .attr('class', (d) => (d.highlighted ? 'link selected-link' : 'link'))
        .attr('id', (d) => `link-${d.source.stepId}-${d.target.stepId}`);

      // Adding the invisible thick link to the group
      // Visible link is added first and invisible link is added later.
      // The order in which the links are added is important as the elements
      // in a group are stacked in the order they are added.
      // The invisible link is responsible for capturing all the mouse events and
      // hence it has to be at the top of the stack
      linkGroup
        .append('path')
        .attr('class', `link-bg ${createdViaContext ? 'disabled-link-bg' : ''}`)
        .attr(
          'id',
          (d) => `link-boundary-${d.source.stepId}-${d.target.stepId}`
        )
        .on('mouseover', (d) => {
          d3.select(`#link-${d.source.stepId}-${d.target.stepId}`).classed(
            'selected-link',
            true
          );

          d3.select(`#arrow-${d.source.stepId}-${d.target.stepId}`).classed(
            'selected-arrow',
            true
          );
          d3.select(`#arrow-${d.target.stepId}-${d.source.stepId}`).classed(
            'selected-arrow',
            true
          );
        })
        .on('mouseout', (d) => {
          d3.select(`#link-${d.source.stepId}-${d.target.stepId}`).classed(
            'selected-link',
            !!d.highlighted
          );

          d3.select(`#arrow-${d.source.stepId}-${d.target.stepId}`).classed(
            'selected-arrow',
            !!d.highlighted
          );
          d3.select(`#arrow-${d.target.stepId}-${d.source.stepId}`).classed(
            'selected-arrow',
            !!d.highlighted
          );
          _this.hideTooltip();
        })
        .on('click', (d) => {
          if (d3.event.defaultPrevented) {
            return;
          }
          this.props.onConnectionClick(d.source, d.target);
          this.GraphStepsSelection?.resetSelectedSteps();
          d3.event.stopPropagation();
        });

      // The arrow for the link
      linkGroup
        .append('path')
        .attr('class', (d) =>
          d.highlighted ? 'arrow selected-arrow' : 'arrow'
        )
        .attr('id', (d) => {
          d.link = d3
            .select(`#link-${d.source.stepId}-${d.target.stepId}`)
            .node();
          return `arrow-${d.source.stepId}-${d.target.stepId}`;
        })
        .attr('d', 'M-15,15L15,0,L-15,-15');
    });

    const toId = this.props.step.id;
    const fromId = this.props.activeConnection;
    d3.select(`#link-${fromId}-${toId}`).classed('selected', true);
    d3.select(`#arrow-${fromId}-${toId}`).classed('selected', true);

    return linksList;
  }

  createNodeContainer(gridcontainer, allSteps) {
    // allSteps is basically this.mapArr
    const container = gridcontainer.append('g').attr('id', 'nodes-group');
    const nodeContextEnabled = BotStore.getEnableNodeContextMenuFlag();

    const nodes = [];

    for (const i in allSteps) {
      if (allSteps.hasOwnProperty(i)) {
        const position =
          allSteps[i].type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE
            ? { x: allSteps[i].x, y: allSteps[i].y }
            : this.getNextEmptyPosition(allSteps[i].x || 0, allSteps[i].y || 0);

        const nodeColors = allSteps[i].color || {
          primary: '#607D8B',
          secondary: '#FFFFFF',
        };

        if (allSteps[i].id === this.props.step.id) {
          this.currentStep = allSteps[i].id;
        }

        let endNodeFlag =
          allSteps[i].step_type[CONTEXT_CLEAR_NODE.KEY] || false;
        if (this.props.nodeContextMenuEnabled || nodeContextEnabled) {
          endNodeFlag = allSteps[i][END_OF_QUERY.KEY]
            ? allSteps[i][END_OF_QUERY.KEY]
            : allSteps[i].step_type[CONTEXT_CLEAR_NODE.KEY];
        }

        nodes.push({
          type: allSteps[i].type,
          stepId: allSteps[i].id,
          subStoryId: allSteps[i].subStoryId,
          title: allSteps[i].title,
          index: allSteps[i].id,
          startNode: allSteps[i].step_type.start,
          endNode: endNodeFlag,
          x: position.x || 0,
          y: position.y || 0,
          destinationList: allSteps[i].destination_list,
          sourceList: allSteps[i].source_list,
          fillColor: nodeColors.primary,
          inactiveColor: nodeColors.transparent,
          titleColor: nodeColors.secondary,
          active: allSteps[i].active,
          systemName: allSteps[i].system_name,
          stepType: allSteps[i].step_type,
          domainName: allSteps[i].domain_name,
          apiFunction: allSteps[i].api_function,
          highlighted: allSteps[i].id === this.props.step.id,
          subStoryActive: allSteps[i].subStoryActive,
          [END_OF_QUERY.KEY]: allSteps[i][END_OF_QUERY.KEY] || false,
        });

        this.idMap[allSteps[i].id] = nodes[i];
        this.setNodePosition(nodes[i].stepId, position.x, position.y);
      }
    }

    const stepEnter = container.selectAll('.steps').data(nodes).enter();

    const createdNodes = this.createNodes(stepEnter);

    return {
      data: nodes,
      group: createdNodes,
    };
  }

  addGridToGroup(group, gridWidth, gridHeight) {
    const groupWidth = 100000;
    const groupHeight = 100000;

    const xScale = d3
      .scaleLinear()
      .domain([0, groupWidth * 3])
      .range([0, groupWidth * 3]);
    const yScale = d3
      .scaleLinear()
      .domain([0, groupHeight * 3])
      .range([0, groupHeight * 3]);

    const xData = xScale
      .ticks(groupWidth * 3)
      .slice(0, Math.floor((groupWidth * 3) / gridWidth) + 1);
    const yData = yScale
      .ticks(groupHeight * 3)
      .slice(0, Math.floor((groupHeight * 3) / gridHeight) + 1);

    const gridGroup = group
      .append('g')
      .attr('id', 'grid-group')
      .attr('class', 'grid');

    gridGroup
      .append('g')
      .selectAll('line')
      .data(xData)
      .enter()
      .append('line')
      .attr('x1', -groupWidth)
      .attr('y1', (d) => Math.floor(d - xData.length / 3) * CONSTANTS.grid_size)
      .attr('x2', groupWidth * 2)
      .attr('y2', (d) => Math.floor(d - xData.length / 3) * CONSTANTS.grid_size)
      .attr('class', 'grid-lines');

    gridGroup
      .append('g')
      .selectAll('line')
      .data(yData)
      .enter()
      .append('line')
      .attr('x1', (d) => Math.floor(d - yData.length / 3) * CONSTANTS.grid_size)
      .attr('y1', -groupHeight)
      .attr('x2', (d) => Math.floor(d - yData.length / 3) * CONSTANTS.grid_size)
      .attr('y2', groupHeight * 2)
      .attr('class', 'grid-lines');
  }

  addMarkersToGroup() {
    const filter = this.grid
      .append('filter')
      .attr('id', 'drop-shadow')
      .attr('height', '150%');

    filter
      .append('feDropShadow')
      .attr('dx', 0)
      .attr('dy', 10)
      .attr('stdDeviation', 10)
      .attr('flood-color', '#000000')
      .attr('flood-opacity', '0.5');
  }

  createMappings() {
    /*
     * mapArr: array with all node data
     * idMap: Object with ids as keys and the values in mapArr as the corresponding data
     */
    this.mapArr = [];
    this.idMap = {};
    this.posMap = [];

    if (this.props.graph.length) {
      for (const i in this.props.graph) {
        if (
          this.props.graph.hasOwnProperty(i) &&
          this.props.graph[i].nodeList
        ) {
          const { nodeList } = this.props.graph[i];
          for (const j in nodeList) {
            if (nodeList.hasOwnProperty(j)) {
              const copyStep = { ...nodeList[j] };
              copyStep.subStoryId = this.props.graph[i].id;
              copyStep.subStoryActive = this.props.graph[i].active;
              this.idMap[nodeList[j]?.id] = copyStep;
              this.mapArr.push(copyStep);
            }
          }
        }
      }
    }
  }

  /**
   * returns current graph width, height, x, y and scale
   * @param  {[string]} selector [selector for one node or the entire graph]
   * @return {[object]} [width, height, x,y, scale]
   */
  getGraphDimension(selector) {
    d3.select('#gridcontainer').attr('transform', null);
    const bounds = d3.select(selector).node().getBoundingClientRect();

    const width = document.getElementById('graph').offsetWidth;
    const height = document.getElementById('graph').offsetHeight - 56; // 56 - top bar height

    // (x, y) indicates the Center point of the bounding Rect
    const x = bounds.x + bounds.width / 2;
    const y = bounds.y + bounds.height / 2;
    let minScale = 0;

    if (bounds.width > bounds.height) {
      minScale = width / 2 / bounds.width;
    } else {
      minScale = height / 2 / bounds.height;
    }

    const scaleDifference = 1 + minScale - this.maxScale;
    const scale = Math.max(
      minScale,
      Math.min(
        this.maxScale,
        // bounds.width / width gives the ratio of graph to viewport
        // As we want to scale to the largest side of the graph, we take the highest ratio
        // If the ratio is greater than 1, that means the graph needs to be scaled down, and vice versa
        // scaleDifference is the outerbound i.e. 1 - (max - min)
        // dividing scaleDifference with the ratio basically gives an interpolated value
        scaleDifference / Math.max(bounds.width / width, bounds.height / height)
      )
    );
    return { scale: parseFloat(scale.toFixed(3)), width, height, x, y };
  }

  /**
   * given a scale, width, height, x, y calculates and returns d3 object
   * with final translate and scale matrix
   * @param {[object]} [dimension]
   */
  defaultZoom({ scale, width, height, x, y }) {
    const translateX = width / 2 - scale * x;
    const translateY = height / 2 - scale * y;
    return d3.zoomIdentity.translate(translateX, translateY).scale(scale);
  }

  /**
   * Get default scale and pan coordinates of the map or the saved one
   * @return {object} [d3 zoom identity object]
   */
  getTransform({ scale, width, height, x, y }) {
    const storyEvent = graphEventStorage.get(this.props.story.id);
    if (storyEvent) {
      const { k: prevScale, x: prevX, y: prevY } = storyEvent.transform;
      return d3.zoomIdentity.translate(prevX, prevY).scale(prevScale);
    }
    return this.defaultZoom({ scale, width, height, x, y });
  }

  zoom() {
    const selector = '#nodes-group';
    if (!document.querySelector(selector)) return;
    if (this.mapArr.length > 0) {
      this.minScale = 0.1;
      this.maxScale = 0.5;
      const { scale, width, height, x, y } = this.getGraphDimension(selector);
      const newMinScale = scale < this.minScale ? scale : this.minScale;
      const newMaxScale = scale > this.maxScale ? scale : this.maxScale;

      this.assignzoom.scaleExtent([newMinScale, newMaxScale]);
      this.grid
        .transition()
        .duration(1000)
        .call(
          this.assignzoom.transform,
          this.props.defaultCenter
            ? this.defaultZoom({ scale, width, height, x, y })
            : this.getTransform({ scale, width, height, x, y })
        );
    }
  }

  /**
   * performs translate and zoom to selected/active node on graph
   * scale value is set different from default zoom
   * scale value can also be fetched from local storage if panOnClick flag is set
   */
  zoomToNode() {
    if (!this.currentStep) return;
    const selector = `#node-${this.currentStep}`;
    if (!document.querySelector(selector)) return;
    let scale = 0.35;
    const { width, height, x, y } = this.getGraphDimension(selector);
    if (this.props.panOnClick) {
      const storyEvent = graphEventStorage.get(this.props.story.id);
      if (storyEvent) {
        scale = storyEvent.transform.k;
      }
    }
    this.grid
      .transition()
      .duration(1000)
      .call(
        this.assignzoom.transform,
        this.defaultZoom({ scale, width, height, x, y })
      );
  }

  createGrid() {
    const elWidth = document.getElementById('graph-wrapper').offsetWidth;
    const elHeight = document.getElementById('graph-wrapper').offsetHeight;
    const _this = this;
    /* If grid is null, initiate it */
    if (!this.grid) {
      // INITIALIZING GRAPH DOM TO S3
      const grid = d3
        .select('#graph')
        .append('svg:svg')
        .attr('class', 'graph-svg')
        .attr('padding', 0)
        .attr('margin', 0)
        .call(this.assignzoom)
        .on('mouseup', () => {
          state.dragLink.active = false;
        })
        .on('click', () => {
          if (d3.event.defaultPrevented) {
            return;
          }

          this.ticked();
        });

      this.grid = grid;
    }

    /* Group the holds everything inside the svg */
    const gridcontainer = this.grid
      .append('g')
      .attr('id', 'gridcontainer')
      .attr('width', elWidth)
      .attr('height', elHeight)
      .attr('top', 0)
      .attr('left', 0)
      .on('mouseup', () => {
        state.dragLink.active = false;
        _this.ticked();
      })
      .on('mousemove', function onMouseMove() {
        if (d3.event.shiftKey) {
          state.dragLink.endPos = d3.mouse(this);
          _this.ticked();
        } else if (state.dragLink.active) {
          state.dragLink.active = false;
          _this.ticked();
        }
      })
      .on('dragover', function dragOver() {
        _this.handleDragHover(d3.mouse(this));
      })
      .on('dragleave', () => {
        _this.disableGhostNode();
      });

    if (this.props.showGrid) {
      this.addGridToGroup(
        gridcontainer,
        CONSTANTS.grid_size,
        CONSTANTS.grid_size
      );
    }

    // drop shadow filter not applied to current design
    // this.addMarkersToGroup();

    this.gridcontainer = gridcontainer;
    this.baseRect = gridcontainer
      .append('rect')
      .attr('height', elHeight)
      .attr('width', elWidth)
      .attr('visibility', 'hidden')
      .style('pointer-events', 'all');

    this.linkGroup = gridcontainer
      .append('g')
      .attr('id', 'links-group')
      .attr('class', 'links-group');

    gridcontainer
      .append('rect')
      .attr('id', 'selected-steps-boundary')
      .attr('height', 0)
      .attr('width', 0)
      .attr('x', 0)
      .attr('y', 0)
      .attr('fill', 'none')
      .attr('stroke', '#0056B8')
      .attr('stroke-width', 5)
      .attr('visibility', 'hidden');

    this.GraphStepsSelection?.updateAllSelectedStepsStyling();
  }

  handleDragHover = (coordinates) => {
    const x = coordinates[0];
    const y = coordinates[1];
    this.ghostNodeGroup.style('visibility', 'visible');
    const draggedPos = snapToGrid(
      x - CONSTANTS.node_width / 2,
      y - CONSTANTS.node_height / 2
    );
    const availablePos = this.getNextEmptyPosition(draggedPos.x, draggedPos.y);
    if (availablePos) {
      this.ghostNodeGroup.attr(
        'transform',
        `translate(${availablePos.x},${availablePos.y})`
      );
    }

    const abTestingState = CommonStore.getABTestingRhsState();
    if (
      CommonStore.getABTestingDrawerFlag() &&
      (abTestingState !== RHS_STATES.TEST_STARTED ||
        (abTestingState === RHS_STATES.TEST_STARTED &&
          CommonStore.getABTestModifiedFlag()))
    ) {
      this.disableGhostNode();
    }
  };

  resetGrid() {
    /* Set width of SVG elements to take up the entire screen
     */

    const elWidth = document.getElementById('graph-wrapper').offsetWidth;
    const elHeight = document.getElementById('graph-wrapper').offsetHeight;
    document.getElementById('graph').height = '100vh';

    /* Wipe the grid since this method is called on a re-render */
    if (this.grid) {
      this.grid.selectAll('*').remove();
      this.grid.attr('height', elHeight).attr('width', elWidth);
    }

    /* Defining the actual D3 zoom action, which has a callback to zoomEvent
     * This is attached to the grid */
    if (!this.assignzoom) {
      this.assignzoom = d3
        .zoom()
        .on('zoom', () => {
          /* Applies the transformation on the gridcontainer group element,
           * not the entire SVG
           */
          graphEventStorage.set(this.props.story.id, d3.event);
          d3.select('#gridcontainer').attr('transform', d3.event.transform);
        })
        .filter(() => {
          return !d3.event.ctrlKey && !d3.event.shiftKey && !d3.event.altKey;
        });
    }
  }

  isConnectionHighlighted = (sourceId, destinationId) => {
    const { connection } = this.props;
    if (connection) {
      return (
        (connection.source === sourceId &&
          connection.target === destinationId) ||
        (connection.source === destinationId && connection.target === sourceId)
      );
    }
    return false;
  };

  createNodesAndLinks(connection = {}) {
    /* Get a set of rendered nodes after they are appended to the DOM */
    this.renderedNodes = this.createNodeContainer(
      this.gridcontainer,
      this.mapArr,
      this.props.graph.length > 0
    );

    /* Get IDs of ALL rendered nodes, across containers */
    const keys = Object.keys(this.idMap);
    const normalLinksData = [];

    for (const i in keys) {
      if (keys.hasOwnProperty(i)) {
        const { sourceList } = this.idMap[keys[i]];
        for (const j in sourceList) {
          if (this.idMap[keys[i]].type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE) {
            this.idMap[keys[i]].destinationList.map((id1) => {
              const index = this.idMap[sourceList[j]].destinationList.findIndex(
                (id2) => id1 === id2 && id2 !== this.idMap[keys[i]].id
              );
              delete this.idMap[sourceList[j]].destinationList[index];
            });
            this.idMap[sourceList[j]].destinationList.push(
              this.idMap[keys[i]].stepId
            );
          }
        }
      }
    }

    /* Create link data */
    for (const i in keys) {
      if (keys.hasOwnProperty(i)) {
        const { destinationList } = this.idMap[keys[i]];
        for (const j in destinationList) {
          if (destinationList.hasOwnProperty(j)) {
            if (this.idMap[destinationList[j]] === this.idMap[keys[i]]) {
              normalLinksData.push({
                source: this.idMap[keys[i]],
                target: this.idMap[keys[i]],
                active: this.idMap[keys[i]].active,
                highlighted: this.isConnectionHighlighted(
                  this.idMap[keys[i]].stepId,
                  this.idMap[keys[i]].stepId
                ),
                subStoryActive: this.idMap[keys[i]].subStoryActive,
                subStoryId: this.idMap[keys[i]].subStoryId,
                destinationSubStoryId: this.idMap[keys[i]].subStoryId,
              });
            } else if (this.idMap[keys[i]] && this.idMap[destinationList[j]]) {
              // Create a link only if the src and target nodes are present
              normalLinksData.push({
                source: this.idMap[keys[i]],
                target: this.idMap[destinationList[j]],
                active:
                  this.idMap[keys[i]].active &&
                  this.idMap[destinationList[j]].active,
                highlighted: this.isConnectionHighlighted(
                  this.idMap[keys[i]].stepId,
                  this.idMap[destinationList[j]].stepId
                ),
                subStoryActive:
                  this.idMap[keys[i]].subStoryActive &&
                  this.idMap[destinationList[j]].subStoryActive,
                subStoryId: this.idMap[keys[i]].subStoryId,
                destinationSubStoryId: this.idMap[destinationList[j]]
                  .subStoryId,
              });
            }
          }
        }
      }
    }

    this.tooltipDiv = d3.select('#tooltip');
    this.hideTooltip();

    // Ghost Node for Node Drag
    this.ghostNodeGroup = this.linkGroup
      .append('g')
      .style('visibility', 'hidden');

    // Self Connected Links
    this.selfLinkGroup = this.linkGroup
      .append('g')
      .attr('id', 'self-link-group');
    this.selfArrowGroup = this.gridcontainer
      .append('g')
      .attr('id', 'self-arrows-group');

    // Regular Links
    this.normalLinkGroup = this.linkGroup
      .append('g')
      .attr('id', 'normal-link-group');

    // Drag Link
    this.dragLinkGroup = this.linkGroup
      .append('g')
      .attr('id', 'drag-link-group');

    this.ghostNodeGroup
      .append('rect')
      .attr('height', VISIBLE_NODE_HEIGHT)
      .attr('width', VISIBLE_NODE_WIDTH)
      .attr('class', 'ghost-node')
      .attr('x', VISIBLE_NODE_X)
      .attr('y', VISIBLE_NODE_Y);

    /* Add lines representing the links */
    this.normalLinks = this.createLinks(
      normalLinksData,
      this.normalLinkGroup,
      this
    );

    this.dragLink = this.dragLinkGroup
      .append('path')
      .attr('class', 'link')
      .attr('stroke', '#565656');

    this.dragArrow = this.dragLinkGroup
      .append('path')
      .attr('class', 'arrow')
      .attr('d', 'M-15,15L15,0,L-15,-15');
  }

  createDragHandler(selection) {
    const _this = this;
    let draggedPos;
    let positionOccupied;

    const changeDraggedPos = (d) => {
      draggedPos = snapToGrid(d3.event.x, d3.event.y);
      positionOccupied = _this.isPositionOccupiedByNode(
        draggedPos.x,
        draggedPos.y,
        d.stepId
      );
      if (positionOccupied) {
        draggedPos = {
          x: d.originalX,
          y: d.originalY,
        };
      }
    };

    function dragStarted(d) {
      d.originalX = d.x;
      d.originalY = d.y;
      changeDraggedPos(d);
    }

    function dragged(d) {
      const changeInX = d3.event.x - d.x;
      const changeInY = d3.event.y - d.y;

      if (_this.ghostNodeGroup.style('visibility') === 'hidden') {
        _this.ghostNodeGroup.style('visibility', 'visible');
      }
      _this.moveTooltip(
        d3.event.sourceEvent.x + 10,
        d3.event.sourceEvent.y - 10
      );

      _this.GraphStepsSelection?.onDrag(d, changeInX, changeInY);

      d3.select(this)
        .attr('cx', (d.x = d3.event.x))
        .attr('cy', (d.y = d3.event.y));

      changeDraggedPos(d);
      _this.ghostNodeGroup.attr(
        'transform',
        `translate(${draggedPos.x},${draggedPos.y})`
      );
      if (selection) {
        selection.attr('transform', (d) => {
          return `translate(${d.x},${d.y})`;
        });
      } else {
        _this.ticked();
      }
    }

    function dragEnded(d) {
      const changeInX = draggedPos.x - d.x;
      const changeInY = draggedPos.y - d.y;

      _this.ghostNodeGroup.style('visibility', 'hidden');

      _this.setNodePosition(
        d.stepId,
        draggedPos.x,
        draggedPos.y,
        d.originalX,
        d.originalY
      );

      d3.select(this)
        .attr('cx', (d.x = draggedPos.x))
        .attr('cy', (d.y = draggedPos.y));

      _this.GraphStepsSelection?.onDragEnd(
        d,
        draggedPos,
        _this.setNodePosition,
        changeInX,
        changeInY
      );

      if (selection) {
        selection.attr(
          'transform',
          `translate(${draggedPos.x},${draggedPos.y})`
        );
      } else {
        _this.ticked();
      }

      if (
        d.originalX - draggedPos.x === 0 &&
        d.originalY - draggedPos.y === 0
      ) {
        return;
      }

      saveNodePositions(d.stepId, d.type, draggedPos.x, draggedPos.y);
    }

    const drag = d3
      .drag()
      .on('start', dragStarted)
      .on('drag', dragged)
      .on('end', dragEnded)
      .filter(() => {
        return (
          !d3.event.ctrlKey &&
          !d3.event.shiftKey &&
          !d3.event.altKey &&
          d3.event.which === 1
        );
      });

    (selection || _this.renderedNodes.group).call(drag);
  }

  nodeSelectionDrag() {
    const _this = this;
    const gridContainer = d3.select('#gridcontainer');

    function onDragStart() {
      const p = d3.mouse(this);
      _this.GraphStepsSelection?.dragStarted(p, gridContainer);
    }

    function onDrag() {
      const p = d3.mouse(this);
      _this.GraphStepsSelection?.dragged(p);
    }

    function onDragEnd() {
      _this.GraphStepsSelection?.dragEnded(
        _this.posMap,
        _this.idMap,
        _this.state.inactiveNodesVisible,
        (d) => {
          if (BotStore.getRHSActive()) {
            navToSubStory(_this.props, d.subStoryId);
            BotStore.setRHSActive(false);
          }
        }
      );
    }

    const dragBehavior = d3
      .drag()
      .on('start', onDragStart)
      .on('drag', onDrag)
      .on('end', onDragEnd)
      .filter(() => {
        return d3.event.metaKey || d3.event.ctrlKey;
      });

    gridContainer.call(dragBehavior);
  }

  ticked() {
    const nodeHeightHalf = CONSTANTS.node_height / 2;
    const nodeWidthHalf = CONSTANTS.node_width / 2;

    if (this.renderedNodes && this.renderedNodes.group) {
      this.renderedNodes.group.attr('transform', (d) => {
        return `translate(${d.x},${d.y})`;
      });
    }

    if (this.normalLinks) {
      this.normalLinks.forEach((linkGroup) => {
        linkGroup.select('path.link').attr('d', (d) => {
          const src = {
            x: d.source.x + nodeWidthHalf,
            y: d.source.y + nodeHeightHalf,
          };
          const dst = {
            x: d.target.x + nodeWidthHalf,
            y: d.target.y + nodeHeightHalf,
          };

          return this.getConnectionPath(src, dst);
        });

        linkGroup.select('path.link-bg').attr('d', (d) => {
          const src = {
            x: d.source.x + nodeWidthHalf,
            y: d.source.y + nodeHeightHalf,
          };
          const dst = {
            x: d.target.x + nodeWidthHalf,
            y: d.target.y + nodeHeightHalf,
          };

          return this.getConnectionPath(src, dst);
        });

        linkGroup.select('path.arrow').attr('transform', (d) => {
          const svgLink = d.link;
          const p1 = svgLink.getPointAtLength(
            svgLink.getTotalLength() * 0.6 - 0.5
          );
          const p2 = svgLink.getPointAtLength(
            svgLink.getTotalLength() * 0.6 + 0.5
          );
          const m = (p2.y - p1.y) / (p2.x - p1.x);
          const angle =
            (Math.atan(m) * 180) / Math.PI + (p2.x >= p1.x ? 0 : 180);
          return `translate(${p1.x},${p1.y})rotate(${angle})`;
        });
      });
    }

    if (this.dragLink) {
      if (state.dragLink.active) {
        this.dragLink.attr('visibility', 'visible').attr('d', () => {
          const src = {
            x: state.dragLink.startNode.x + nodeWidthHalf,
            y: state.dragLink.startNode.y + nodeHeightHalf,
          };
          const dst = {
            x: state.dragLink.endPos[0],
            y: state.dragLink.endPos[1],
          };

          return this.getConnectionPath(src, dst);
        });

        this.dragArrow.attr('visibility', 'visible').attr('transform', () => {
          const tempLink = this.dragLink.node();
          const p1 = tempLink.getPointAtLength(
            tempLink.getTotalLength() * 0.6 - 0.5
          );
          const p2 = tempLink.getPointAtLength(
            tempLink.getTotalLength() * 0.6 + 0.5
          );
          const m = (p2.y - p1.y) / (p2.x - p1.x);
          const angle =
            (Math.atan(m) * 180) / Math.PI + (p2.x >= p1.x ? 0 : 180);
          return `translate(${p1.x},${p1.y})rotate(${angle})`;
        });
      } else {
        this.dragLink.attr('visibility', 'hidden');
        this.dragArrow.attr('visibility', 'hidden');
      }
    }

    const containerBBox = this.renderedNodes.group._parents[0].getBBox();
    if (this.baseRect) {
      const mouseRectPadding = 5000;
      this.baseRect
        .attr('x', containerBBox.x - mouseRectPadding)
        .attr('y', containerBBox.y - mouseRectPadding)
        .attr('width', containerBBox.width + mouseRectPadding * 2)
        .attr('height', containerBBox.height + mouseRectPadding * 2);
    }
  }

  centerToNode(stepToFocus) {
    d3.select(`#node-${this.currentStep}`).classed('is-highlighted', false);
    d3.select(`#node-${stepToFocus}`).classed(
      'is-highlighted',
      !this.GraphStepsSelection?.getSelectedSteps()?.[stepToFocus]
    );
    this.currentStep = stepToFocus;
    this.zoomToNode();
  }

  toggleInactiveNodes() {
    d3.selectAll('.inactive').classed(
      'hidden',
      this.state.inactiveNodesVisible
    );
  }

  addNode(node, isReplace) {
    const position = isReplace
      ? { x: node.x, y: node.y }
      : this.getNextEmptyPosition(node.x || 0, node.y || 0);
    const nodeColors = node.color || {
      primary: '#607D8B',
      transparent: '#607D8B',
      secondary: '#FFFFFF',
    };

    const nodes = [
      {
        stepId: node.id,
        subStoryId: node.subStoryId,
        index: node.id,
        title: node.title,
        startNode: node.step_type.start,
        endNode: this.props.nodeContextMenuEnabled
          ? node[END_OF_QUERY.KEY] || false
          : node.step_type[CONTEXT_CLEAR_NODE.KEY],
        x: position.x,
        y: position.y,
        destinationList: node.destination_list,
        sourceList: node.source_list,
        fillColor: nodeColors.primary,
        inactiveColor: nodeColors.transparent,
        titleColor: nodeColors.secondary,
        active: node.active,
        systemName: node.system_name,
        stepType: node.step_type,
        domainName: node.domain_name,
        apiFunction: node.api_function,
        highlighted: node.id === this.props.step.id,
        subStoryActive: true,
        type: node.type,
        [END_OF_QUERY.KEY]: node[END_OF_QUERY.KEY] || false,
      },
    ];

    this.idMap[node.id] = nodes[0];
    this.setNodePosition(nodes[0].stepId, position.x, position.y);
    const stepEnter = d3.select('#nodes-group').data(nodes);
    const newNode = this.createNodes(stepEnter);
    this.createDragHandler(newNode);
  }

  createConnection(fromId, toId, cb = () => {}, createdViaContext = false) {
    this.props.updateConnections();
    const normalLinksData = [];
    normalLinksData.push({
      source: this.idMap[fromId],
      target: this.idMap[toId],
      active: this.idMap[fromId].active && this.idMap[toId].active,
      subStoryActive:
        this.idMap[fromId].subStoryActive && this.idMap[toId].subStoryActive,
      subStoryId: this.idMap[fromId].subStoryId,
      destinationSubStoryId: this.idMap[toId].subStoryId,
    });
    const createdLinks = this.createLinks(
      normalLinksData,
      d3.select('#normal-link-group'),
      this,
      true,
      createdViaContext
    );
    this.normalLinks = this.normalLinks.concat(createdLinks);
    this.ticked();
    cb();
  }

  deleteNode(nodeToDelete) {
    d3.select(`#node-${nodeToDelete.id}`).remove();
    if (
      this.posMap[nodeToDelete.x] &&
      this.posMap[nodeToDelete.x][nodeToDelete.y]
    ) {
      this.posMap[nodeToDelete.x][nodeToDelete.y] = null;
    }
  }

  toggleNode(node) {
    d3.select(`#node-${node.id}`)
      .classed('steps', true)
      .classed('inactive-steps', node.active)
      .classed('inactive', node.active)
      .classed('hidden', node.active && !this.state.inactiveNodesVisible)
      .classed('substory-inactive', !node.subStoryActive)
      .classed('is-highlighted', (d) => d.highlighted);

    d3.select(`#node-${node.id}`)
      .select('.graph-step-title')
      .style('text-decoration', !node.active ? '' : 'line-through');

    d3.select(`#node-${node.id}`)
      .select('.step-background-rect')
      .attr(
        'stroke',
        !node.active ? 'var(--primary-color)' : CONSTANTS.node_inactive_color
      )
      .attr(
        'fill',
        !node.active ? 'var(--primary-color)' : CONSTANTS.node_inactive_color
      );

    (node.destination_list || []).map((destinationNode) => {
      const link = d3.select(`#link-${node.id}-${destinationNode}`);
      const isInactive = node.active || !this.idMap[destinationNode].active;
      const linkWrapper = link.select(function getParentNode() {
        return this.parentNode;
      });
      linkWrapper
        .classed('hidden', isInactive && !this.state.inactiveNodesVisible)
        .classed('inactive', isInactive);
    });

    (node.source_list || []).map((sourceNode) => {
      const link = d3.select(`#link-${sourceNode}-${node.id}`);
      const isInactive = node.active || !this.idMap[sourceNode].active;
      const linkWrapper = link.select(function getParentNode() {
        return this.parentNode;
      });
      linkWrapper
        .classed('hidden', isInactive && !this.state.inactiveNodesVisible)
        .classed('inactive', isInactive);
    });
  }

  updateNode(node) {
    this.updateNodeTitle(node.id, node.title);
    this.updateNodeFlags(node.id, node.step_type);
    if (
      this.idMap[node.id] &&
      this.idMap[node.id].active !== node.step_status.active
    ) {
      node.active = !node.step_status.active;
      node.destination_list = this.idMap[node.id].destinationList;
      node.source_list = this.idMap[node.id].sourceList;
      node.subStoryActive = this.idMap[node.id].subStoryActive;
      this.toggleNode(node);
      this.idMap[node.id].active = !node.active;
    }
  }

  toggleInactiveNodesVisibility() {
    if (!this.state.inactiveNodesVisible) {
      const mixPanelTracker = BotStore.getMixPanel();
      mixPanelTracker.track({
        event: MIX_PANEL.GRAPH.SHOW_INACTIVE_NODES,
      });
    }

    storeUserPreference(
      STORAGE_KEYS.INACTIVE_NODES_VISIBLE,
      !this.state.inactiveNodesVisible
    );
    this.setState({
      inactiveNodesVisible: !this.state.inactiveNodesVisible,
    });

    const gaLabel = this.state.inactiveNodesVisible
      ? GOOGLE_ANALYTICS.EVENTS.HIDE_INACTIVE
      : GOOGLE_ANALYTICS.EVENTS.SHOW_INACTIVE;
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.GRAPH,
      gaLabel,
      this.props.domain.title,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
  }

  updateNodeFlags(id, stepType) {
    d3.select(`#node-${id}`)
      .select('.end-circle')
      .attr(
        'visibility',
        stepType[CONTEXT_CLEAR_NODE.KEY] ? 'visible' : 'hidden'
      );
    d3.select(`#node-${id}`)
      .select('.end-text')
      .attr(
        'visibility',
        stepType[CONTEXT_CLEAR_NODE.KEY] ? 'visible' : 'hidden'
      );

    d3.select(`#node-${id}`)
      .select('.start-circle')
      .attr('visibility', stepType.start ? 'visible' : 'hidden');
    d3.select(`#node-${id}`)
      .select('.start-text')
      .attr('visibility', stepType.start ? 'visible' : 'hidden');
  }

  updateNodeTitle(id, title) {
    let updatedTitle = title;
    if (updatedTitle.length > CONSTANTS.string_max_length) {
      updatedTitle = `${title.substring(0, CONSTANTS.string_max_length)}...`;
    }

    if (this.idMap[id]) {
      this.idMap[id].title = title;
    }
    d3.select(`#node-${id}`).select('.graph-step-title').text(updatedTitle);
  }

  refresh() {
    this.createMappings();
    this.resetGrid();
    this.createGrid();
    this.createNodesAndLinks();
    this.createDragHandler();
    this.nodeSelectionDrag();
    this.ticked();
    this.zoom();
  }

  componentDidMount() {
    if (this.props.graph.length > 0) {
      this.createMappings();
      this.resetGrid();
      this.createGrid();
      this.createNodesAndLinks();
      this.createDragHandler();
      this.nodeSelectionDrag();
      this.ticked();
      this.zoom();
    }
    this.isSuperman = isNewPipeline(this.props.domain);
    this.GraphStepsSelection =
      BotStore.getEnvironment() !== ENVIRONMENT.PRODUCTION
        ? new GraphStepsSelection()
        : null;
    GraphStore.setGraphStepsSelection(this.GraphStepsSelection);
    GraphStore.setSelectedSteps({});
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.step.id !== nextProps.step.id) {
      this.centerToNode(nextProps.step.id);
    }

    // Refresh graph when the bot has been changed via URL
    if (this.props.domain.id !== nextProps.domain.id) {
      setTimeout(() => {
        this.refresh();
      }, 1000);
      this.GraphStepsSelection?.resetSelectedSteps();
      if (this.state.isKeyboardShortcutsActive) {
        this.toggleKeyboardShortcuts();
      }
    }

    if (this.props.allSteps.length < nextProps.allSteps.length) {
      if (nextProps.allSteps.length - this.props.allSteps.length === 1) {
        // If only 1 new node is being added, then add directly.
        this.newNode = {
          ...nextProps.allSteps[nextProps.allSteps.length - 1],
        };

        this.newNode.subStoryId = this.props.subStory.id;
        if (this.newNode.newNode) {
          if (this.newNode.type === GRAPH_NODE_DISPLAY_TEXT.AB_NODE) {
            this.addNode(this.newNode);
          } else {
            const modifiedNode = getDropPosition(this.newNode);
            !CommonStore.getABTestingDrawerFlag() &&
              setTimeout(
                () =>
                  updateCurrentStepPosition({
                    x: modifiedNode.x,
                    y: modifiedNode.y,
                    id: modifiedNode.id,
                  }),
                50
              );
            this.addNode(modifiedNode);
          }
          this.disableGhostNode();
        } else
          setTimeout(() => {
            this.refresh();
          }, 1000);
      } else {
        // If multiple nodes with/without connections are being added as done for templates,
        // then refresh the whole graph.
        setTimeout(() => {
          this.refresh();
        }, 0);
      }
    }

    const hasNewNode = nextProps.allSteps[
      nextProps.allSteps.length - 1
    ].id.includes('new');
    if (this.props.connection !== nextProps.connection && !hasNewNode) {
      const { connection: prevVal } = this.props;
      const { connection: nextVal } = nextProps;
      if (
        prevVal.source !== nextVal.source ||
        prevVal.target !== nextVal.target
      ) {
        setTimeout(() => {
          this.refresh();
        }, 100);
      }
    }

    if (
      hasNewNode &&
      this.GraphStepsSelection &&
      Object.keys(this.GraphStepsSelection.getSelectedSteps()).length > 0
    ) {
      this.GraphStepsSelection.resetSelectedSteps();
    }

    if (this.props.allSteps.length === nextProps.allSteps.length + 1) {
      const nodeToDelete = this.props.allSteps[this.props.allSteps.length - 1];
      const modifiedNode = getDropPosition(nodeToDelete);
      this.deleteNode(modifiedNode);
      this.newNode = undefined;
    }

    if (this.props.activeConnection !== nextProps.activeConnection) {
      const toId = nextProps.step.id;
      const fromId = nextProps.activeConnection;
      d3.select('.link.selected').classed('selected', false);
      d3.select('.arrow.selected').classed('selected', false);
      d3.select(`#link-${fromId}-${toId}`).classed('selected', true);
      d3.select(`#arrow-${fromId}-${toId}`).classed('selected', true);
    }

    const { connectFrom } = this;
    const connectTo = nextProps.allSteps[nextProps.allSteps.length - 1].id;
    if (this.props.nodeContextMenuEnabled) {
      if (connectFrom && connectTo) {
        if (hasNewNode) {
          this.createConnection(
            connectFrom,
            connectTo,
            () => {
              document
                .getElementById(`node-${connectTo}`)
                .setAttribute('data-disabled', true);
            },
            true
          );
        } else if (
          !nextProps.step.id.includes('new') &&
          nextProps.step.id &&
          this.contextConnectionTriggered
        ) {
          this.contextConnectionTriggered = false;

          createNewConnection(
            GRAPH_NODE_TYPE[this.contextConnectionData.from],
            connectFrom,
            GRAPH_NODE_TYPE[this.connectToNodeType],
            nextProps.step.id,
            this.props.currentLanguage.languageSource,
            this.props.currentLanguage.languageScript
          )
            .then(() => {
              this.nodeCreationActive = false;
              // Need this temp variable to remove warning when marking as End Node just for first iteration
              this.connectFromCopyTemp = connectFrom;
              setTimeout(() => {
                // Fetching Graph again because the stored graph doesn't have recently added connections via Context Menu.
                // Because of which it disappears when a graph refresh is made by some action.
                fetchGraph(this.props.story.id, () => {
                  updateGraph({
                    REFRESH: true,
                  });
                });
                this.createConnection(connectFrom, nextProps.step.id, () => {
                  this.centerToNode(nextProps.step.id);
                  document
                    .getElementsByClassName(`warning-tooltip-${connectFrom}`)[0]
                    .setAttribute('visibility', 'hidden');
                  this.connectFrom = '';
                });
              }, 1000);
            })
            .catch((error) => {
              this.connectFrom = '';
              this.nodeCreationActive = false;
              createAlert(error, null, AlertTypes.NEGATIVE);
            });
        } else {
          this.refresh();
          this.nodeCreationActive = false;
          setTimeout(() => {
            this.connectFrom = '';
          }, 1500);
        }
      }
    }
  }

  componentWillUpdate(nextProps, nextState) {
    if (nextState.inactiveNodesVisible !== this.state.inactiveNodesVisible) {
      this.toggleInactiveNodes();
    }
  }

  disableGhostNode = () => {
    this.ghostNodeGroup.style('visibility', 'hidden');
  };

  update(event) {
    if (event.CONNECTION_UPDATE && event.CONNECTION_UPDATE.fromNodeId) {
      this.createConnection(
        event.CONNECTION_UPDATE.fromNodeId,
        this.props.step.id
      );
    } else if (event.NODE_TOGGLED) {
      this.toggleNode(event.NODE_TOGGLED.step);
    } else if (event.SUBSTORY_TOGGLED) {
      const { subStoryToToggle } = event.SUBSTORY_TOGGLED;
      d3.selectAll(`.substory-${subStoryToToggle.id}`).classed(
        'substory-inactive',
        subStoryToToggle.active
      );
    } else if (event.NODE_UPDATED) {
      this.updateNode(event.NODE_UPDATED.step);
    } else if (event.REFRESH) {
      this.refresh();
    }
  }

  handleClick = (e) => {
    switch (e.currentTarget.id) {
      case 'toggle-inactive':
        this.toggleInactiveNodesVisibility();
        break;

      default:
        break;
    }
  };

  /**
   * removes previously stored graph
   * transformation and recenters it
   */
  recenterGraph = () => {
    if (graphEventStorage.get(this.props.story.id)) {
      graphEventStorage.delete(this.props.story.id);
      this.zoom();
      sendGoogleAnalyticsEvents(
        GOOGLE_ANALYTICS.CATEGORY.GRAPH,
        GOOGLE_ANALYTICS.EVENTS.RECENTER,
        this.props.domain.title,
        GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
      );
    }
  };

  handleDragEnter = () => {
    this.props.setDroppable(true);
  };

  handleDragOver = (e) => {
    e.preventDefault();
  };

  handleDragLeave = (e) => {
    if (e.pageX === 0 && e.pageY === 0) return;
    this.props.setDroppable(false);
  };

  getNodeConnections = async (stepId) => {
    const response = await Api.getConnectionsToNodeAsync(stepId);
    const { STATIC, CODE, OUTPUT } = GRAPH_NODE_DISPLAY_TEXT;
    if (response.success) {
      const connectedTo = response.body.to_nodes;
      if (connectedTo.length > 0) {
        if (
          this.contextConnectionData.from === STATIC ||
          this.contextConnectionData.from === OUTPUT
        ) {
          connectedTo.map((item) => {
            if (item.type === 'processing') {
              this.contextConnectionData.to = CODE;
            } else {
              this.contextConnectionData.to = STATIC;
            }
          });
        }
      } else {
        this.contextConnectionData.to = '';
      }
      this.setState({
        contextLoaderActive: false,
      });
      this.forceUpdate();
    } else {
      this.setState({
        contextLoaderActive: false,
      });
      createAlert('Something went wrong', null, AlertTypes.NEGATIVE);
      this.forceUpdate();
    }
  };

  toggleContextMenu = (attachTo, active, stepId, stepType) => {
    if (!active) {
      if (this.contextMenuTrigger) {
        this.contextMenuTrigger.style = {
          opacity: 0,
          transform: 'translateX(5px)',
          pointerEvents: 'none',
        };
      }

      setTimeout(() => {
        Array.from(document.querySelectorAll('.contextActive')).forEach((el) =>
          el.classList.remove('contextActive')
        );
      }, 100);
    } else {
      const mixPanelTracker = BotStore.getMixPanel();
      mixPanelTracker.track({
        event: MIX_PANEL.NODE.CONTEXT_MENU_OPENED,
        payload: {
          'From Node Type': stepType,
        },
      });
    }

    this.contextMenuTrigger = attachTo || null;
    this.setState({
      contextMenuActive: active,
      contextLoaderActive: active,
    });

    if (stepId) {
      this.contextConnectionData.from = stepType;
      this.getNodeConnections(stepId);
    }
    this.forceUpdate();
  };

  toggleWarningTooltip = (attachTo, nodeData) => {
    const warningInfo = getWarningInfo(attachTo ? nodeData : null);
    this.warningIconTrigger = attachTo || null;

    if (!attachTo) {
      setTimeout(() => {
        this.warningIconText = '';
      }, 200);
    } else {
      this.warningIconText = warningInfo?.msg;
    }

    this.setState({
      warningIconCoords: attachTo ? attachTo.getBoundingClientRect() : {},
      warningIconActive: !!attachTo,
    });

    this.forceUpdate();
  };

  contextMenuItemClick = (nodeType) => {
    const { from } = this.contextConnectionData;
    const nodeData = this.contextMenuTrigger.__data__;
    const nodePos = document
      .getElementById(`node-${nodeData.stepId}`)
      .getBoundingClientRect();

    this.connectToNodeType = nodeType;
    this.connectFrom = nodeData.stepId;
    this.connectFromCopy = nodeData.stepId;
    nodeData.type = nodeType;
    GraphStore.setCurrentSubstoryId(nodeData.subStoryId);
    this.nodeCreationActive = true;
    this.contextConnectionTriggered = true;
    this.props.onNewNode(nodePos.x + 20, nodePos.y, nodeType, nodeData);

    const mixPanelTracker = BotStore.getMixPanel();
    mixPanelTracker.track({
      event: MIX_PANEL.NODE.CONTEXT_MENU_ITEM_CLICKED,
      payload: {
        'From Node Type': from,
        'To Node Type': nodeData.type,
      },
    });

    this.toggleContextMenu(null, false);
  };

  toggleKeyboardShortcuts = () => {
    this.setState({
      isKeyboardShortcutsActive: !this.state.isKeyboardShortcutsActive,
    });
  };

  renderContextMenuOptions = () => {
    const { from, to } = this.contextConnectionData;
    const { STATIC, CODE, OUTPUT } = GRAPH_NODE_DISPLAY_TEXT;

    if (from === STATIC) {
      if (to === CODE) {
        return <ContextMenu.Item title="No options present" disabled />;
      }

      return (
        <>
          {(to === STATIC || to === '') && (
            <ContextMenu.Item
              onClick={() => {
                this.contextMenuItemClick(STATIC);
              }}
              title="Connect to New Static Step"
            />
          )}

          {this.isSuperman && to === '' && (
            <ContextMenu.Item
              onClick={() => {
                this.contextMenuItemClick(CODE);
              }}
              title="Connect to New Code Step"
            />
          )}

          {to === '' && (
            <ContextMenu.Item
              onClick={() => {
                const nodeData = this.contextMenuTrigger.__data__;
                const domainName = this.props.domain.title;
                const domainId = this.props.domain.id;
                const storyId = this.props.story.id;
                const { subStoryId } = nodeData;

                this.setState({
                  contextMenuActive: false,
                });

                const {
                  stepType,
                  x,
                  y,
                  active,
                  systemName,
                  title,
                  stepId,
                } = nodeData;

                if (this.props.nodeContextMenuEnabled) {
                  stepType.query_end = true;
                } else {
                  stepType.context_clear = true;
                }

                // default values
                const stepAttr = 'Permanent';
                const modelName = '';
                const taskId = 0;

                createNewStep(
                  stepId,
                  title,
                  stepType,
                  stepAttr,
                  active,
                  systemName,
                  modelName,
                  taskId,
                  subStoryId,
                  domainName,
                  storyId,
                  domainId,
                  x,
                  y,
                  (err) => {
                    if (err) return;
                    createAlert('Step saved.', null, AlertTypes.POSITIVE);
                    const mixPanelTracker = BotStore.getMixPanel();
                    mixPanelTracker.track({
                      event: MIX_PANEL.NODE.CONTEXT_MENU_ITEM_CLICKED,
                      payload: {
                        'Marked as End of Query': true,
                      },
                    });

                    fetchGraph(storyId, () => {
                      updateGraph({
                        REFRESH: true,
                      });
                    });

                    this.createConnection(this.connectFromCopy, stepId);
                    this.connectFromCopyTemp &&
                      document
                        .getElementsByClassName(
                          `warning-tooltip-${this.connectFromCopyTemp}`
                        )[0]
                        .setAttribute('visibility', 'hidden');
                  }
                );
              }}
              title="Mark as End of Query"
            />
          )}
        </>
      );
    }

    if (from === CODE) {
      return (
        <ContextMenu.Item
          onClick={() => {
            this.contextMenuItemClick(OUTPUT);
          }}
          title="Connect to New Output Step"
        />
      );
    }

    if (from === OUTPUT) {
      return (
        <>
          <ContextMenu.Item
            onClick={() => {
              this.contextMenuItemClick(STATIC);
            }}
            title="Connect to New Static Step"
          />

          {to !== STATIC && (
            <ContextMenu.Item
              onClick={() => {
                const nodeData = this.contextMenuTrigger.__data__;
                const domainName = this.props.domain.title;
                const domainId = this.props.domain.id;
                const storyId = this.props.story.id;
                const { subStoryId } = nodeData;

                this.setState({
                  contextMenuActive: false,
                });

                const {
                  stepType,
                  x,
                  y,
                  active,
                  systemName,
                  title,
                  stepId,
                } = nodeData;

                if (this.props.nodeContextMenuEnabled) {
                  stepType.query_end = true;
                } else {
                  stepType.context_clear = true;
                }

                // default values
                const stepAttr = 'Permanent';
                const modelName = '';
                const taskId = 0;

                createNewStep(
                  stepId,
                  title,
                  stepType,
                  stepAttr,
                  active,
                  systemName,
                  modelName,
                  taskId,
                  subStoryId,
                  domainName,
                  storyId,
                  domainId,
                  x,
                  y,
                  (err) => {
                    if (err) return;
                    createAlert('Step saved.', null, AlertTypes.POSITIVE);
                    const mixPanelTracker = BotStore.getMixPanel();
                    mixPanelTracker.track({
                      event: MIX_PANEL.NODE.CONTEXT_MENU_ITEM_CLICKED,
                      payload: {
                        CAS: CASName(),
                        'Marked as End of Query': true,
                      },
                    });
                    this.refresh();

                    fetchGraph(storyId, () => {
                      updateGraph({
                        REFRESH: true,
                      });
                    });

                    this.createConnection(this.connectFromCopy, stepId);
                    this.connectFromCopyTemp &&
                      document
                        .getElementsByClassName(
                          `warning-tooltip-${this.connectFromCopyTemp}`
                        )[0]
                        .setAttribute('visibility', 'hidden');
                  }
                );
              }}
              title="Mark as End of Query"
            />
          )}
        </>
      );
    }
  };

  render() {
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    const style = {
      width: '100%',
      height: '100%',
      display: 'block',
    };

    const containerStyle = {
      width: 'fit-content',
      columnGap: 8,
      transition: 'all 0.3s ease',
      position: 'fixed',
      left: 0,
      bottom: 20,
      right: 0,
      margin: '0 auto',
      background: 'transparent',
      zIndex: 2,
    };

    return (
      <div
        className={`${
          this.props.className ? this.props.className : ''
        } graphview ${hasSelfServeLayout ? 'graphview_ww_noHeader' : ''}`}
        style={this.props.style}
        onDragEnter={this.handleDragEnter}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
      >
        {hasSelfServeLayout ? (
          <Box style={{ ...containerStyle }}>
            <Tooltip
              themed
              tooltip={`${
                this.state.inactiveNodesVisible ? 'Hide' : 'Show'
              } Inactive Steps`}
              position="TOP"
            >
              <GraphButton
                id="toggle-inactive"
                onClick={this.handleClick}
                type="graph"
              >
                <Icon
                  src={this.state.inactiveNodesVisible ? HideIcon : ShowIcon}
                />
              </GraphButton>
            </Tooltip>

            <Tooltip themed tooltip="Recenter Graph" position="TOP">
              <GraphButton onClick={this.recenterGraph} type="graph">
                <Icon src={RecenterWWIcon} />
              </GraphButton>
            </Tooltip>

            <Tooltip themed tooltip="Keyboard Shortcuts" position="TOP">
              <GraphButton onClick={this.toggleKeyboardShortcuts} type="graph">
                <KeyboardIcon />
              </GraphButton>
            </Tooltip>
          </Box>
        ) : (
          <div className="toolbar row">
            <div
              className="toolaction-wrapper toolaction"
              id="toggle-inactive"
              onClick={this.handleClick}
            >
              <Icon
                className="toolaction-icon"
                src={this.state.inactiveNodesVisible ? 'invisible' : 'visible'}
                tooltip={
                  this.state.inactiveNodesVisible
                    ? GRAPH_HIDE_INACTIVE_NODES_TOOLTIP.text
                    : GRAPH_SHOW_INACTIVE_NODES_TOOLTIP.text
                }
              />
              <span className="toolaction-text">
                {this.state.inactiveNodesVisible ? 'Hide ' : 'Show '}
                Inactive Steps
              </span>
            </div>
            <div
              className="toolaction-wrapper toolaction"
              onClick={this.recenterGraph}
            >
              <Icon
                className="toolaction-icon"
                src={RecenterIcon}
                tooltip="Re-center graph"
              />
              <span className="toolaction-text">Re-center</span>
            </div>
          </div>
        )}
        {this.state.isKeyboardShortcutsActive && (
          <KeyboardShortcuts
            toggleKeyboardShortcuts={this.toggleKeyboardShortcuts}
          />
        )}

        <div className="graph" id="graph" style={style}>
          <div
            className="tooltip"
            id="tooltip"
            style={{ opacity: 0, pointerEvents: null }}
          />
        </div>

        {this.props.nodeContextMenuEnabled && (
          <>
            <ContextMenu
              onClose={() => {
                this.toggleContextMenu(null, false);
              }}
              active={this.state.contextMenuActive}
              attachTo={this.contextMenuTrigger}
              offset={{ top: 5, left: 5 }}
              prefixClassName={
                this.state.contextLoaderActive
                  ? 'graphContextLoader'
                  : 'graphContext'
              }
            >
              {this.state.contextLoaderActive ? (
                <Loader />
              ) : (
                this.renderContextMenuOptions()
              )}
            </ContextMenu>

            <NodeWarning
              active={this.state?.warningIconActive}
              position={{
                left: this.state?.warningIconCoords?.left - 30,
                top: this.state?.warningIconCoords?.top - 50,
              }}
              text={this.warningIconText}
            />
          </>
        )}
      </div>
    );
  }
}

GraphView.propTypes = {
  story: PropTypes.object,
  subStory: PropTypes.object,
  domain: PropTypes.object,
  step: PropTypes.object,
  graph: PropTypes.arrayOf(PropTypes.object),
  allSteps: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  activeConnection: PropTypes.string,
  style: PropTypes.object,
  onNodeClick: PropTypes.func,
  onConnectionClick: PropTypes.func,
};

export default GraphView;
